import { MRT_ColumnDef } from "mantine-react-table"
import { ResourceImage } from "./ResourceImage"
import { ResourceTypeTreeSelect } from "./ResourceTypeTreeSelect"
import { ExtendedInfiniteScrollingTableNewApi } from "../../-components/infinite-scrolling-table-new-api/extended-infinite-scrolling-table"
import { NumberInput, TextInput } from "@mantine/core"
import { useNavigate, useSearch } from "@tanstack/react-router"
import useGetResourceFieldTypes from "./useGetResourceFieldTypes"
import { ResourceTypeCategory } from "../../../../@client/Theasoft_DataClasses_Dispo"
import {
  FieldData,
  FieldFilter,
  getUpdatedFilterDict,
} from "../../../../-components/field-data"
import { useTranslation } from "react-i18next"
import { getFileType } from "@/utils/getFileType"
import { ResourceDto, ResourceFilter } from "@/orval/gen/model"
import { getPaginatedResources } from "@/orval/gen"

export function ResourcesTable({
  category,
}: {
  category: ResourceTypeCategory
}) {
  const { data: fieldTypes } = useGetResourceFieldTypes(category)
  const search = useSearch({ from: "/app/resources/resources" })
  const { filter } = search
  const navigate = useNavigate()
  const updateFilter = (newValue: ResourceFilter) =>
    navigate({
      to: "/app/resources/resources",
      search: { filter: { ...filter, ...newValue } },
      replace: true,
    })
  const updateSearch = (newValue: Partial<typeof search>) => {
    navigate({
      to: "/app/resources/resources",
      search: { ...search, ...newValue },
      replace: true,
    })
  }

  React.useEffect(() => {
    updateFilter({ resourceTypeTreeNodes: undefined })
  }, [category])
  const { t } = useTranslation("app_server")

  const columns = React.useMemo<MRT_ColumnDef<ResourceDto>[]>(
    () => [
      {
        accessorKey: "resourceType",
        header: t("Type"),
        Filter: () => (
          <ResourceTypeTreeSelect
            category={category}
            flex={1}
            onCheckedStateChange={(values) =>
              updateFilter({ resourceTypeTreeNodes: values })
            }
            initialCheckedState={filter?.resourceTypeTreeNodes ?? []}
          />
        ),
      },
      {
        accessorKey: "name",
        header: t("Name"),
        Filter: () => (
          <TextInput
            value={filter?.name ?? undefined}
            onChange={(e) => updateFilter({ name: e.target.value })}
          />
        ),
      },
      {
        accessorKey: "count",
        header: t("Amount"),
        Filter: () => (
          <NumberInput
            value={filter?.count ?? undefined}
            onChange={(value) =>
              typeof value === "number"
                ? updateFilter({ count: value })
                : updateFilter({ count: undefined })
            }
          />
        ),
      },
      {
        accessorKey: "availableCount",
        header: t("Available"),
        Filter: () => (
          <NumberInput
            value={filter?.availableCount ?? undefined}
            onChange={(value) =>
              typeof value === "number"
                ? updateFilter({ availableCount: value })
                : updateFilter({ availableCount: undefined })
            }
          />
        ),
      },
      {
        accessorKey: "attachmentId",
        header: t("Picture"),
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          const image = row.original.attachments.find(
            (a) => getFileType(a.fileName) === "image"
          )
          if (!image) return null
          return (
            <ResourceImage
              attachmentId={image.attachmentId}
              resourceId={row.original.resourceId}
              h={"100%"}
              w={"100%"}
              mah={100}
            />
          )
        },
      },
      ...fieldTypes.map<MRT_ColumnDef<ResourceDto>>((type) => ({
        accessorKey: type.name,
        header: type.name,
        enableSorting: false,
        Cell: (cell) => (
          <FieldData
            fieldData={
              cell.row.original.resourceFields
                ?.flatMap((rf) => rf.value)
                .find((field) => field?.name == type.name) ?? undefined
            }
          />
        ),
        Filter: () => (
          <FieldFilter
            fieldType={type}
            value={filter?.fieldFilter?.[type.id].values}
            onChange={(value) =>
              updateFilter({
                fieldFilter: getUpdatedFilterDict(
                  type.id,
                  { dataType: type.dataType, values: value },
                  filter?.fieldFilter
                ),
              })
            }
          />
        ),
      })),
    ],
    [fieldTypes, filter]
  )
  return (
    <ExtendedInfiniteScrollingTableNewApi
      queryFn={getPaginatedResources}
      defaultSorting={[{ id: "resourceId", desc: false }]}
      columnDefinitions={columns}
      getParams={(pageRequest) => ({
        ...pageRequest,
        ...search,
        filter: { ...filter, category },
      })}
      sorting={search.sorting ?? []}
      onSortingChange={(value) => updateSearch({ sorting: value })}
      globalFilter={search.globalFilter ?? ""}
      onGlobalFilterChange={(value) => updateSearch({ globalFilter: value })}
      onClickRow={(row) =>
        navigate({
          to: "/app/resources/$id",
          params: { id: row.resourceId.toString() },
        })
      }
    />
  )
}
